import type { ReactNode } from 'react';
import { Avatar } from '../Avatar';
import { CollapsedSideRail } from '../side-rail/CollapsedSideRail';
import { SideRailCollapseButton } from '../side-rail/SideRailCollapseButton';
import { useSideRailCollapsed } from '../side-rail/useSideRailCollapsed';

const PATIENT_CARD_COLLAPSED_STORAGE_KEY = 'odo_files_viewer_patient_card_collapsed';
const PATIENT_CARD_DEFAULT_COLLAPSED = false;
const PATIENT_CARD_PANEL_NAME = 'patient card';
const RAIL_FALLBACK_LABEL = 'Patient';
const MAX_RAIL_NAME_LENGTH = 28;
const SECONDARY_LABEL_SEPARATOR = ' · ';

interface CollapsiblePatientCardProps {
    patientName: string;
    patientEmail?: string;
    doctorName?: string | null;
    practiceName?: string | null;
    profilePictureUrl?: string;
    profileThumbnailUrl?: string;
    children: (collapseButton: ReactNode) => ReactNode;
}

function buildRailLabel(patientName: string): string {
    const name = patientName.trim();
    return name && name.length <= MAX_RAIL_NAME_LENGTH ? name : RAIL_FALLBACK_LABEL;
}

function presentNames(...names: (string | null | undefined)[]): string[] {
    return names.map((name) => name?.trim() ?? '').filter(Boolean);
}

function buildRailTooltip(patientName: string, careTeam: string[]): ReactNode {
    return (
        <div className="flex flex-col gap-0.5 font-inter">
            <span className="text-[12px] font-semibold text-gray-900 leading-tight">
                {patientName.trim() || RAIL_FALLBACK_LABEL}
            </span>
            {careTeam.map((name) => (
                <span key={name} className="text-[11px] text-gray-500 leading-tight">
                    {name}
                </span>
            ))}
        </div>
    );
}

function CollapsiblePatientCard({
    patientName,
    patientEmail,
    doctorName,
    practiceName,
    profilePictureUrl,
    profileThumbnailUrl,
    children
}: CollapsiblePatientCardProps) {
    const { collapsed, toggle } = useSideRailCollapsed(
        PATIENT_CARD_COLLAPSED_STORAGE_KEY,
        PATIENT_CARD_DEFAULT_COLLAPSED
    );

    if (collapsed) {
        const careTeam = presentNames(doctorName, practiceName);
        return (
            <CollapsedSideRail
                side="left"
                panelName={PATIENT_CARD_PANEL_NAME}
                label={buildRailLabel(patientName)}
                secondaryLabel={careTeam.join(SECONDARY_LABEL_SEPARATOR) || undefined}
                labelTooltip={buildRailTooltip(patientName, careTeam)}
                onExpand={toggle}
                leading={
                    <div className="w-7 h-7">
                        <Avatar
                            user={{ name: patientName, email: patientEmail ?? '' }}
                            imageURL={profilePictureUrl}
                            thumbnailURL={profileThumbnailUrl}
                        />
                    </div>
                }
            />
        );
    }

    return (
        <div className="w-1/4 min-w-[260px] max-w-[320px] bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.08)] overflow-hidden">
            {children(
                <SideRailCollapseButton
                    side="left"
                    panelName={PATIENT_CARD_PANEL_NAME}
                    onCollapse={toggle}
                />
            )}
        </div>
    );
}

export { CollapsiblePatientCard };
